import {
  Aggregations,
  Bucket,
  FacetItem,
  FacetModel,
  FacetsList,
  FilterSelections,
  NumericRange,
  ResultItem,
  Results,
  SearchResponse,
  SearchShapeResponse
} from '../../../types/Search';
import {SearchFieldOption} from "../../../components/search/search-field/SearchField";
import {AggregationType} from "../../../types/AggregationType";
import AggregationUtil from './aggregation-util';

const INDEX_NAME_PREFIX = 'otp-';

/**
 * Utility for dealing with search response to internal model conversion.
 */
export default class SearchResponseConverter {

  static buildFacetsList(searchResponse: SearchResponse, facetsList: FacetsList, selections: FilterSelections) {

    const isSelected = (facetItemKey: string) => {
      return !!selections[facetItemKey];
    };

    // human: {hits: [...], aggregations: {...}}
    Object.values(searchResponse).forEach((response: SearchShapeResponse) => {
      const aggregations: Aggregations = response.aggregations || {};

      Object.keys(aggregations).forEach((aggregationKey: string) => {
        const aggregation = aggregations[aggregationKey];
        if (aggregation.buckets.length === 0) {
          return;
        }

        const facet: FacetModel = this.createFacetModel(aggregationKey);

        const buckets = aggregation.buckets || [];
        buckets.forEach((bucket: Bucket) => {
          const bucketKey = this.getBucketKey(bucket, facet.facetType);
          const facetItemKey = AggregationUtil.buildFacetItemKey(facet.field, facet.facetType, bucketKey);
          const rangeData: NumericRange = {min: bucket.min, max: bucket.max};
          const selected = isSelected(facetItemKey);

          facet.data[facetItemKey] = {
            key: bucketKey,
            count: bucket.doc_count,
            selected,
            rangeData
          } as FacetItem;

          facet.hasSelection = facet.hasSelection || selected;
        });

        if (facet.field === "type") {
          // push the type facet model on top because we need it to be first
          facetsList.unshift(facet);
        } else {
          facetsList.push(facet);
        }
      });
    });
  }

  private static createFacetModel(aggregationKey: string): FacetModel {
    // E.g. keyword-Planet-homeworld
    const {aggregationType, fieldRange, fieldName} = AggregationUtil.splitAggregationName(aggregationKey);
    return {
      facetType: aggregationType,
      field: fieldName,
      fieldType: fieldRange,
      title: fieldName,
      data: {},
      hasSelection: false
    };
  }

  private static getBucketKey(bucket: Bucket, aggregationType: AggregationType): any {
    // The date ranges also return key_as_string but we'll use the epoch time for now
    if (aggregationType === AggregationType.DATERANGE) {
      return bucket.key;
    }
    return bucket.key_as_string || bucket.key;
  }

  public static buildSearchResultsModel(data: SearchResponse, results: Results): void {
    const prefixLength = INDEX_NAME_PREFIX.length;
    Object.keys(data).forEach((type: string) => {
      const resultItems = data[type].hits;
      results.total = data[type].total;
      results.count = resultItems.length;
      resultItems.forEach((resultItem: any) => {
        const model = resultItem.root;
        const index = resultItem.index ? resultItem.index.substring(prefixLength) : '';
        const item: ResultItem = {
          id: model.id,
          name: model.name,
          type: resultItem.type,
          index
        };
        results.resultItems.push(item);
      });
    });
  }

  public static convertAutoSuggestResponseToModel(data: any, results: Array<SearchFieldOption>): void {
    const searchResults: Array<any> = data.root_search.hits;
    searchResults.forEach((result: any) => {
      results.push({
        id: result.root.id,
        value: result.root.id,
        label: result.root.name || result.root.id,
        type: result.type
      });
    });
  }
}
